import type { Product } from 'payments-api/model';
import { useGetRolesSuspense } from 'api/payments/generated/fragmentPaymentsAPI';
import clsx from 'clsx';
import { PaymentViewLayout } from 'components/Layout/PaymentViewLayout';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { capitalize } from 'lodash-es';
import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Icon } from '@fragment/ui/src/components/Icon/BaseIcon/Icon';
import { TabsV2 } from '@fragment/ui/src/components/Tabs/TabsV2';

import { BillingToggle } from './BillingToggle';
import { CatalogProductTable } from './CatalogProductTable';
import { ALL_ROLES_VALUE } from './constants';
import { ProductDetailModal } from './ProductDetailModal';

const ViewToggle = ({
  viewMode,
  setViewMode,
  readOnly,
}: {
  viewMode?: 'grid' | 'list';
  setViewMode?: (m: 'grid' | 'list') => void;
  readOnly?: boolean;
}) => (
  <BillingToggle
    readOnly={readOnly}
    initialActiveButton={viewMode === 'list' ? 'right' : 'left'}
    onLeftClick={() => setViewMode?.('grid')}
    onRightClick={() => setViewMode?.('list')}
    leftLabel={<Icon type="gridView" size="sm" />}
    rightLabel={<Icon type="listView" size="sm" />}
    buttonPadding="py-f0"
    buttonHeight="h-fit"
  />
);

const RolesTabs = ({
  selectedRoleName,
  setSelectedRoleName,
  rightSlot,
}: {
  selectedRoleName: string;
  setSelectedRoleName: (roleName: string) => void;
  rightSlot?: React.ReactNode;
}) => {
  const { data: rolesData } = useGetRolesSuspense();

  const roleFilterOptions = useMemo(
    () => [
      { value: ALL_ROLES_VALUE, label: 'All' },
      ...rolesData.data
        .map((r) => ({ value: r.role, label: capitalize(r.role) }))
        .sort((a, b) => a.value.localeCompare(b.value)),
    ],
    [rolesData.data]
  );

  return (
    <div className="flex items-end w-full">
      <TabsV2
        options={roleFilterOptions}
        selectedValue={selectedRoleName}
        onClick={setSelectedRoleName}
        borderColor="main-300"
      />
      <div className="flex-1 border-b-[0.5px] border-main-300" />
      {rightSlot}
    </div>
  );
};

const TILE_BORDER = 'border-r-[0.5px] border-b-[0.5px] border-main-300';
const FILLER_BORDER = '';
const GRID_CONTAINER =
  'grid grid-cols-[repeat(auto-fill,minmax(320px,1fr))] w-full min-h-[360px]';

const SKELETON_MAX_TILES = 12;

type Props = {
  workspaceId: string;
};

const CatalogPageLoading = () => {
  const [visibleTiles, setVisibleTiles] = useState(0);
  const directionRef = useRef(1);

  useEffect(() => {
    const timer = setInterval(() => {
      setVisibleTiles((prev) => {
        const next = prev + directionRef.current;
        if (next >= SKELETON_MAX_TILES) {
          directionRef.current = -1;
          return SKELETON_MAX_TILES;
        }
        if (next <= 0) {
          directionRef.current = 1;
          return 0;
        }
        return next;
      });
    }, 120);
    return () => clearInterval(timer);
  }, []);

  return (
    <PaymentViewLayout
      title="Catalog"
      primaryAction={{ title: 'New', onClick: () => {}, disabled: true }}
    >
      <div className="flex flex-col w-full min-h-0">
        <div className="sticky top-0 z-10 shrink-0">
          <div className="flex items-end w-full">
            <div className="flex-1 border-b-[0.5px] border-main-300 py-f1 text-xmd min-h-[2.5rem] [line-height:1]" />
            <div className="flex items-center border-l-[0.5px] border-b-[0.5px] border-main-300 px-f1 self-stretch">
              <ViewToggle readOnly />
            </div>
          </div>
        </div>
        <div className={GRID_CONTAINER}>
          <div className={clsx('aspect-[8/9]', TILE_BORDER)} />
          {Array.from({ length: visibleTiles }).map((_, i) => (
            <div
              // eslint-disable-next-line react/no-array-index-key
              key={`skeleton-${i}`}
              className={clsx('aspect-[8/9]', TILE_BORDER)}
            />
          ))}
          {Array.from({ length: 8 }).map((_, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={`filler-${i}`} className={FILLER_BORDER} />
          ))}
        </div>
      </div>
    </PaymentViewLayout>
  );
};

const CatalogListPageContent = ({ workspaceId }: Props): JSX.Element => {
  useDocumentTitle('Catalog');

  const [selectedRoleName, setSelectedRoleName] =
    useState<string>(ALL_ROLES_VALUE);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showDraftProduct, setShowDraftProduct] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  const onProductClick = useCallback((product: Product) => {
    setSelectedProduct(product);
  }, []);

  return (
    <>
      <PaymentViewLayout
        title="Catalog"
        primaryAction={{
          title: 'Add',
          onClick: () => setShowDraftProduct(true),
          disabled: selectedRoleName === ALL_ROLES_VALUE || showDraftProduct,
        }}
      >
        <div className="flex flex-col w-full h-full min-h-0">
          <div className="sticky top-0 z-10 shrink-0">
            <Suspense
              fallback={
                <div className="flex items-end w-full">
                  <div className="flex-1 border-b-[0.5px] border-main-300 py-f1 text-xmd min-h-[2.5rem] [line-height:1]" />
                  <div className="flex items-center border-l-[0.5px] border-b-[0.5px] border-main-300 px-f1 self-stretch">
                    <ViewToggle readOnly />
                  </div>
                </div>
              }
            >
              <RolesTabs
                selectedRoleName={selectedRoleName}
                setSelectedRoleName={setSelectedRoleName}
                rightSlot={
                  <div className="flex items-center border-l-[0.5px] border-b-[0.5px] border-main-300 px-f1 self-stretch">
                    <ViewToggle viewMode={viewMode} setViewMode={setViewMode} />
                  </div>
                }
              />
            </Suspense>
          </div>
          <div className="flex flex-col w-full min-h-0 flex-1">
            <CatalogProductTable
              selectedRoleName={selectedRoleName}
              onProductClick={onProductClick}
              viewMode={viewMode}
              showDraftProduct={showDraftProduct}
              onDraftClose={() => setShowDraftProduct(false)}
            />
          </div>
        </div>
      </PaymentViewLayout>
      {selectedProduct && (
        <ProductDetailModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
        />
      )}
    </>
  );
};

export const CatalogPage = ({ workspaceId }: Props): JSX.Element => (
  <Suspense fallback={<CatalogPageLoading />}>
    <CatalogListPageContent workspaceId={workspaceId} />
  </Suspense>
);
